import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { BASE_CRM_URL } from "../../../../components/config/config";
import Reminders from "./ViewComponents/Reminders";
import Notes from "./ViewComponents/Notes";
import Tasks from "./ViewComponents/Tasks";
import ActiveLog from "./ViewComponents/ActiveLog";
import dayjs from "dayjs";
import CommonFooter from "../../../../../core/common/footer/commonFooter";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "./logo.png";
import ImageWithBasePath from "../../../../../core/img/imagewithbasebath";
import { Modal, Input, message } from "antd";
import QuillEditor from "./QuillEditor";

const formatDate = (date) => {
  if (!date) return "N/A";

  const d = dayjs(date);

  if (!d.isValid() || d.year() < 2000) {
    return "N/A";
  }

  return d.format("DD/MM/YYYY");
};

const formatMoney = (value) => {
  const number = Number(value || 0);

  return number.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const generateProposalPDF = (viewDetails) => {
  if (!viewDetails) return;

  const doc = new jsPDF("p", "mm", "a4");

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const margin = 14;
  const usableWidth = pageWidth - margin * 2;

  let y = 18;

  const customerX = pageWidth / 2 + 10;

  const companyBlockMaxWidth = customerX - margin - 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);

  doc.text(viewDetails.CompanyName || "Company Name", margin, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  y += 6;

  const writeCompanyLine = (text) => {
    const lines = doc.splitTextToSize(text, companyBlockMaxWidth);

    doc.text(lines, margin, y);

    y += lines.length * 4.5;
  };

  if (viewDetails.CompanyAddress) {
    writeCompanyLine(viewDetails.CompanyAddress);
  }

  const companyLocation = [
    viewDetails.CompanyCity,
    viewDetails.CompanyPostalCode,
  ]
    .filter(Boolean)
    .join(" - ");

  if (companyLocation) {
    writeCompanyLine(companyLocation);
  }

  if (viewDetails.CompanyCountryCode) {
    writeCompanyLine(viewDetails.CompanyCountryCode);
  }

  if (viewDetails.CompanyPhoneNumber) {
    writeCompanyLine(viewDetails.CompanyPhoneNumber);
  }

  if (viewDetails.CompanyGSTIN) {
    writeCompanyLine(`GSTIN: ${viewDetails.CompanyGSTIN}`);
  }

  let customerY = 18;

  const customerBlockMaxWidth = pageWidth - margin - customerX;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);

  doc.text("TO", customerX, customerY);

  customerY += 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);

  doc.text(viewDetails.ClientName || "N/A", customerX, customerY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  customerY += 5;

  if (viewDetails.MailId) {
    doc.text(`Email: ${viewDetails.MailId}`, customerX, customerY);

    customerY += 5;
  }

  if (viewDetails.PhoneNumber) {
    doc.text(`Phone: ${viewDetails.PhoneNumber}`, customerX, customerY);

    customerY += 5;
  }

  const customerAddress = [
    viewDetails.billing_street,
    viewDetails.billing_city,
    viewDetails.billing_state,
    viewDetails.billing_zip,
    viewDetails.CountryName,
  ]
    .filter(Boolean)
    .join(", ");

  if (customerAddress) {
    const addressLines = doc.splitTextToSize(
      customerAddress,
      customerBlockMaxWidth,
    );

    doc.text(addressLines, customerX, customerY);

    customerY += addressLines.length * 4.5;
  }

  const companySectionHeight = Math.max(y, customerY);

  doc.setLineWidth(0.4);

  doc.line(
    margin,
    companySectionHeight + 5,
    pageWidth - margin,
    companySectionHeight + 5,
  );

  y = companySectionHeight + 14;

  const labelX = margin;
  const valueX = margin + 25;
  const valueMaxWidth = pageWidth - margin - valueX;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);

  doc.text("Subject:", labelX, y);

  doc.setFont("helvetica", "normal");

  const subjectLines = doc.splitTextToSize(
    viewDetails.subject || "N/A",
    valueMaxWidth,
  );

  doc.text(subjectLines, valueX, y);

  y += subjectLines.length * 5 + 2;

  doc.setFont("helvetica", "bold");

  doc.text("Proposal No:", labelX, y);

  doc.setFont("helvetica", "normal");

  doc.text(viewDetails.PrefixNumber || `PRO-${viewDetails.id}`, valueX, y);

  y += 7;

  doc.setFont("helvetica", "bold");

  doc.text("Date:", labelX, y);

  doc.setFont("helvetica", "normal");

  doc.text(formatDate(viewDetails.datecreated), valueX, y);

  y += 7;

  doc.setFont("helvetica", "bold");

  doc.text("Open Till:", labelX, y);

  doc.setFont("helvetica", "normal");

  doc.text(formatDate(viewDetails.open_till), valueX, y);

  y += 7;

  y += 3;

  if (typeof viewDetails.content === "string" && viewDetails.content.trim()) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);

    doc.text("Content", margin, y);

    y += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    const contentLines = doc.splitTextToSize(viewDetails.content, usableWidth);

    doc.text(contentLines, margin, y);

    y += contentLines.length * 5 + 5;
  }

  const items = viewDetails.items || [];

  const tableData = items.map((item, index) => {
    const qty = Number(item.qty || 0);
    const rate = Number(item.rate || 0);

    const amount = qty * rate;

    const taxes = (item.item_taxes || []).map((tax) => tax.taxname).join(", ");

    return [
      index + 1,
      item.description || "N/A",
      item.long_description || "",
      qty,
      item.unit || "",
      formatMoney(rate),
      taxes || "N/A",
      formatMoney(amount),
    ];
  });

  autoTable(doc, {
    startY: y,

    head: [
      ["#", "Item", "Description", "Qty", "Unit", "Rate", "Tax", "Amount"],
    ],

    body: tableData,

    theme: "grid",

    styles: {
      font: "helvetica",
      fontSize: 7.5,
      cellPadding: 2.5,
      valign: "middle",
      overflow: "linebreak",
    },

    headStyles: {
      fontStyle: "bold",
      fontSize: 8,
    },

    columnStyles: {
      0: {
        cellWidth: 8,
        halign: "center",
      },

      1: {
        cellWidth: 24,
      },

      2: {
        cellWidth: 40,
      },

      3: {
        cellWidth: 12,
        halign: "center",
      },

      4: {
        cellWidth: 16,
      },

      5: {
        cellWidth: 22,
        halign: "right",
      },

      6: {
        cellWidth: 32,
      },

      7: {
        cellWidth: 28,
        halign: "right",
      },
    },

    margin: {
      left: margin,
      right: margin,
    },
  });

  const totalsTableWidth = 75;
  const totalsLeft = pageWidth - margin - totalsTableWidth;

  let totalsY = doc.lastAutoTable.finalY + 8;

  if (totalsY > pageHeight - 55) {
    doc.addPage();
    totalsY = 20;
  }

  const currency = viewDetails.currencyName || "";

  const discountPercent = Number(viewDetails.discount_percent || 0);

  const totalsData = [
    ["Subtotal:", `${currency} ${formatMoney(viewDetails.subtotal)}`],
    [
      discountPercent ? `Discount (${discountPercent}%):` : "Discount:",
      `- ${currency} ${formatMoney(viewDetails.discount_total)}`,
    ],
    ["Tax:", `${currency} ${formatMoney(viewDetails.total_tax)}`],
    ["Adjustment:", `${currency} ${formatMoney(viewDetails.adjustment)}`],
  ];

  autoTable(doc, {
    startY: totalsY,

    body: totalsData,

    theme: "plain",

    tableWidth: totalsTableWidth,

    margin: {
      left: totalsLeft,
      right: margin,
    },

    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: {
        top: 2,
        bottom: 2,
        left: 0,
        right: 0,
      },
      textColor: [0, 0, 0],
    },

    columnStyles: {
      0: {
        cellWidth: 40,
        halign: "left",
      },

      1: {
        cellWidth: 35,
        halign: "right",
      },
    },
  });

  totalsY = doc.lastAutoTable.finalY + 3;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);

  doc.line(totalsLeft, totalsY, pageWidth - margin, totalsY);

  totalsY += 8;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);

  doc.text("TOTAL:", totalsLeft, totalsY);

  doc.text(
    `${currency} ${formatMoney(viewDetails.total)}`,
    pageWidth - margin,
    totalsY,
    {
      align: "right",
    },
  );

  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);

    doc.text(viewDetails.CompanyName || "", margin, pageHeight - 8);

    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 8, {
      align: "right",
    });
  }

  // const proposalNumber = viewDetails.PrefixNumber || `PRO-${viewDetails.id}`;

  // doc.save(`${proposalNumber}.pdf`);
  return doc;
};

const rs = (value) => `Rs.${formatMoney(value)}`;

const getStatusColor = (status) => {
  const normalized = (status || "").toLowerCase();

  if (normalized === "paid") return [22, 163, 74];
  if (normalized === "overdue") return [200, 40, 40];
  if (normalized === "unpaid" || normalized === "partially paid")
    return [200, 130, 20];
  return [100, 100, 100];
};

const extractTaxName = (taxname) =>
  (taxname || "").replace(/^[\d.]+%\s*/, "").trim() || taxname || "Tax";

export const generateInvoicePDF = (viewDetails) => {
  if (!viewDetails) return;

  const doc = new jsPDF("p", "mm", "a4");

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const margin = 14;
  const usableWidth = pageWidth - margin * 2;
  const items = viewDetails.items || [];

  const invoiceNumber =
    viewDetails.InvoiceNumber ||
    (viewDetails.PrefixNumber || "").replace(/\s+/g, "") ||
    `INV-${viewDetails.id}`;

  let y = 16;
  let logoBottom = y;

  const logoWidth = 42;
  const logoHeight = 14;
  doc.addImage(logo, margin, y, logoWidth, logoHeight);
  logoBottom = y + logoHeight;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("INVOICE", pageWidth - margin, y + 6, { align: "right" });

  doc.setFontSize(10);
  doc.text(`# ${invoiceNumber}`, pageWidth - margin, y + 12, {
    align: "right",
  });

  if (viewDetails.InvoiceStatus) {
    const [r, g, b] = getStatusColor(viewDetails.InvoiceStatus);
    doc.setTextColor(r, g, b);
    doc.setFontSize(10);
    doc.text(
      viewDetails.InvoiceStatus.toUpperCase(),
      pageWidth - margin,
      y + 18,
      {
        align: "right",
      },
    );
    doc.setTextColor(0, 0, 0);
  }

  y = Math.max(logoBottom, y + 22) + 4;

  const billToX = pageWidth - margin;
  const colMaxWidth = pageWidth / 2 - margin - 6;

  let companyY = y;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(viewDetails.CompanyName || "Company Name", margin, companyY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  companyY += 5;

  const writeLeftLine = (text) => {
    const lines = doc.splitTextToSize(text, colMaxWidth);
    doc.text(lines, margin, companyY);
    companyY += lines.length * 4.3;
  };

  if (viewDetails.CompanyAddress) writeLeftLine(viewDetails.CompanyAddress);

  const companyCityLine = [viewDetails.CompanyCity, viewDetails.CompanyState]
    .filter(Boolean)
    .join(" ");
  if (companyCityLine) writeLeftLine(companyCityLine);

  const companyCountryLine = [
    viewDetails.CompanyCountryCode,
    viewDetails.CompanyPostalCode,
  ]
    .filter(Boolean)
    .join(" ");
  if (companyCountryLine) writeLeftLine(companyCountryLine);

  if (viewDetails.CompanyGSTIN)
    writeLeftLine(`GSTIN: ${viewDetails.CompanyGSTIN}`);

  const companyPhones = [
    viewDetails.CompanyPhoneNumber,
    viewDetails.CompanyMobileNumber
      ? `${viewDetails.CompanyMobileNumber} (M)`
      : null,
  ]
    .filter(Boolean)
    .join(", ");
  if (companyPhones) writeLeftLine(companyPhones);

  let billY = y;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Bill To:", billToX, billY, { align: "right" });

  billY += 5;

  doc.text(viewDetails.ClientName || "N/A", billToX, billY, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  billY += 5;

  const clientAddress = [viewDetails.billing_street, viewDetails.billing_city]
    .filter(Boolean)
    .join(", ");
  if (clientAddress) {
    const lines = doc.splitTextToSize(clientAddress, colMaxWidth);
    doc.text(lines, billToX, billY, { align: "right" });
    billY += lines.length * 4.3;
  }

  const clientStateLine = viewDetails.billing_state || "";
  if (clientStateLine) {
    doc.text(clientStateLine, billToX, billY, { align: "right" });
    billY += 4.3;
  }

  const clientCountryLine = [
    viewDetails.billing_country_code || viewDetails.CountryName,
    viewDetails.billing_zip,
  ]
    .filter(Boolean)
    .join(" ");
  if (clientCountryLine) {
    doc.text(clientCountryLine, billToX, billY, { align: "right" });
    billY += 4.3;
  }

  const clientGSTIN = viewDetails.ClientGSTIN || viewDetails.CustomerGSTIN;
  if (clientGSTIN) {
    doc.text(`GSTIN: ${clientGSTIN}`, billToX, billY, { align: "right" });
    billY += 4.3;
  }

  billY += 4;

  doc.text(
    `Invoice Date: ${formatDate(viewDetails.date || viewDetails.datecreated)}`,
    billToX,
    billY,
    { align: "right" },
  );
  billY += 4.5;

  doc.text(
    `Due Date: ${formatDate(viewDetails.duedate || viewDetails.open_till)}`,
    billToX,
    billY,
    { align: "right" },
  );
  billY += 4.5;

  y = Math.max(companyY, billY) + 8;

  const colWidths = {
    num: 8,
    item: 78,
    qty: 22,
    rate: 24,
    tax: 30,
    amount: 20,
  };

  const tableData = items.map((item, index) => {
    const qty = Number(item.qty || 0);
    const rate = Number(item.rate || 0);
    const amount = qty * rate;

    const taxLines = (item.item_taxes || [])
      .map(
        (t) =>
          `${extractTaxName(t.taxname)} ${Number(t.taxrate || 0).toFixed(2)}%`,
      )
      .join("\n");

    const itemCellText = [
      item.description || "N/A",
      item.long_description || "",
    ]
      .filter(Boolean)
      .join("\n");

    return [
      index + 1,
      itemCellText,
      `${qty}${item.unit ? ` ${item.unit}` : ""}`,
      formatMoney(rate),
      taxLines || "N/A",
      formatMoney(amount),
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [["#", "Item", "Qty", "Rate", "Tax", "Amount"]],
    body: tableData,
    theme: "plain",

    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
      valign: "top",
      overflow: "linebreak",
    },

    headStyles: {
      fillColor: [31, 41, 55],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8.5,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
    },

    columnStyles: {
      0: { cellWidth: colWidths.num, halign: "center" },
      1: { cellWidth: colWidths.item },
      2: { cellWidth: colWidths.qty, halign: "center" },
      3: { cellWidth: colWidths.rate, halign: "right" },
      4: { cellWidth: colWidths.tax },
      5: { cellWidth: colWidths.amount, halign: "right" },
    },

    margin: { left: margin, right: margin },

    didDrawCell: (data) => {
      if (data.section !== "body" || data.column.index !== 1) return;

      const item = items[data.row.index];
      if (!item) return;

      doc.setFillColor(255, 255, 255);
      doc.rect(
        data.cell.x,
        data.cell.y,
        data.cell.width,
        data.cell.height,
        "F",
      );

      const textX = data.cell.x + 2.5;
      let textY = data.cell.y + 4.5;
      const maxWidth = data.cell.width - 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      const titleLines = doc.splitTextToSize(
        item.description || "N/A",
        maxWidth,
      );
      doc.text(titleLines, textX, textY);
      textY += titleLines.length * 3.7;

      if (item.long_description) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);

        item.long_description.split("\n").forEach((line) => {
          const wrapped = doc.splitTextToSize(line, maxWidth);
          doc.text(wrapped, textX, textY);
          textY += wrapped.length * 3.5;
        });
      }
    },
  });

  /*

   * 4. TOTALS: SUB TOTAL, PER-TAX BREAKDOWN, TOTAL, PAID, DUE

   */

  const totalsWidth = 75;
  const totalsLeft = pageWidth - margin - totalsWidth;

  let totalsY = doc.lastAutoTable.finalY + 6;

  if (totalsY > pageHeight - 70) {
    doc.addPage();
    totalsY = 20;
  }

  const subtotal = Number(viewDetails.subtotal || 0);
  const discountPercent = Number(viewDetails.discount_percent || 0);
  const discountTotal = Number(viewDetails.discount_total || 0);

  const taxTotals = new Map();
  items.forEach((item) => {
    const amount = Number(item.qty || 0) * Number(item.rate || 0);
    (item.item_taxes || []).forEach((t) => {
      const name = extractTaxName(t.taxname);
      const rate = Number(t.taxrate || 0);
      const key = `${name}_${rate}`;
      const taxAmount = (amount * rate) / 100;
      taxTotals.set(key, {
        name,
        rate,
        amount: (taxTotals.get(key)?.amount || 0) + taxAmount,
      });
    });
  });

  const paymentsArray = Array.isArray(viewDetails.payments)
    ? viewDetails.payments
    : Array.isArray(viewDetails.Transactions)
      ? viewDetails.Transactions
      : [];

  const totalPaid =
    viewDetails.total_paid !== undefined
      ? Number(viewDetails.total_paid)
      : paymentsArray.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const amountDue =
    viewDetails.amount_due !== undefined
      ? Number(viewDetails.amount_due)
      : Number(viewDetails.total || 0) - totalPaid;

  const totalsRows = [{ label: "Sub Total", value: rs(subtotal) }];

  if (discountPercent || discountTotal) {
    totalsRows.push({
      label: discountPercent ? `Discount (${discountPercent}%)` : "Discount",
      value: `- ${rs(discountTotal)}`,
    });
  }

  Array.from(taxTotals.values()).forEach((t) => {
    totalsRows.push({
      label: `${t.name} (${t.rate.toFixed(2)}%)`,
      value: rs(t.amount),
    });
  });

  totalsRows.push({
    label: "Total",
    value: rs(viewDetails.total),
    shaded: true,
    bold: true,
  });

  if (totalPaid || paymentsArray.length) {
    totalsRows.push({
      label: "Total Paid",
      value: `-${rs(totalPaid)}`,
      shaded: true,
    });
    totalsRows.push({
      label: "Amount Due",
      value: rs(amountDue),
      shaded: true,
      bold: true,
    });
  }

  const rowHeight = 6.5;

  totalsRows.forEach((row) => {
    if (row.shaded) {
      doc.setFillColor(237, 237, 237);
      doc.rect(totalsLeft, totalsY - 4.5, totalsWidth, rowHeight, "F");
    }

    doc.setFont("helvetica", row.bold ? "bold" : "normal");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    doc.text(row.label, totalsLeft + 2, totalsY);
    doc.text(row.value, pageWidth - margin - 2, totalsY, { align: "right" });

    totalsY += rowHeight;
  });

  let afterTotalsY = totalsY + 6;

  /*

   * 5. TRANSACTIONS

   */

  if (paymentsArray.length) {
    if (afterTotalsY > pageHeight - 50) {
      doc.addPage();
      afterTotalsY = 20;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Transactions:", margin, afterTotalsY);

    afterTotalsY += 6;

    const txRows = paymentsArray.map((p) => [
      String(p.payment_id ?? p.id ?? p.paymentid ?? ""),
      p.paymentmode || p.PaymentMode || p.mode || "N/A",
      formatDate(p.date),
      rs(p.amount),
    ]);

    autoTable(doc, {
      startY: afterTotalsY,
      head: [["Payment #", "Payment Mode", "Date", "Amount"]],
      body: txRows,
      theme: "plain",
      styles: { font: "helvetica", fontSize: 8.5, cellPadding: 2 },
      headStyles: { fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 50 },
        2: { cellWidth: 42 },
        3: { cellWidth: 50, halign: "left" },
      },
      margin: { left: margin, right: margin },
    });

    afterTotalsY = doc.lastAutoTable.finalY + 8;
  }

  /*

   * 6. NOTE + TERMS & CONDITIONS

   */

  if (afterTotalsY > pageHeight - 40) {
    doc.addPage();
    afterTotalsY = 20;
  }

  if (
    typeof viewDetails.clientnote === "string" &&
    viewDetails.clientnote.trim()
  ) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Note:", margin, afterTotalsY);

    afterTotalsY += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);

    const noteLines = doc.splitTextToSize(viewDetails.clientnote, usableWidth);
    doc.text(noteLines, margin, afterTotalsY);

    afterTotalsY += noteLines.length * 4.3 + 6;
  }

  if (typeof viewDetails.terms === "string" && viewDetails.terms.trim()) {
    if (afterTotalsY > pageHeight - 30) {
      doc.addPage();
      afterTotalsY = 20;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Terms & Conditions:", margin, afterTotalsY);

    afterTotalsY += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    viewDetails.terms
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .forEach((line) => {
        const wrapped = doc.splitTextToSize(line, usableWidth);
        doc.text(wrapped, margin, afterTotalsY);
        afterTotalsY += wrapped.length * 4 + 1.5;
      });
  }

  afterTotalsY += 10;

  if (afterTotalsY > pageHeight - 20) {
    doc.addPage();
    afterTotalsY = 30;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(
    "Authorized Signature _________________________",
    margin,
    afterTotalsY,
  );

  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);

    doc.text(viewDetails.CompanyName || "", margin, pageHeight - 8);

    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 8, {
      align: "right",
    });
  }

  // doc.save(`${invoiceNumber}.pdf`);
  return doc;
};

const getEstimateStatusColor = (status) => {
  const normalized = (status || "").toLowerCase();

  if (normalized === "accepted") return [22, 163, 74];
  if (normalized === "declined") return [200, 40, 40];
  if (normalized === "expired") return [200, 130, 20];
  if (normalized === "sent") return [37, 99, 235];
  return [100, 100, 100];
};

export const generateEstimatePDF = (viewDetails) => {
  if (!viewDetails) return;

  const doc = new jsPDF("p", "mm", "a4");

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const margin = 14;
  const usableWidth = pageWidth - margin * 2;

  const items = viewDetails.items || [];

  const estimateNumber =
    (viewDetails.PrefixNumber || "").replace(/\s+/g, "") ||
    `${viewDetails.prefix || "EST-"}${viewDetails.number || viewDetails.id}`;

  let y = 16;
  let logoBottom = y;

  const logoWidth = 42;
  const logoHeight = 14;
  doc.addImage(logo, margin, y, logoWidth, logoHeight);
  logoBottom = y + logoHeight;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("ESTIMATE", pageWidth - margin, y + 6, { align: "right" });

  doc.setFontSize(10);
  doc.text(`# ${estimateNumber}`, pageWidth - margin, y + 12, {
    align: "right",
  });

  let statusLineY = y + 18;

  if (viewDetails.StatusName) {
    const [r, g, b] = getEstimateStatusColor(viewDetails.StatusName);
    doc.setTextColor(r, g, b);
    doc.setFontSize(10);
    doc.text(
      viewDetails.StatusName.toUpperCase(),
      pageWidth - margin,
      statusLineY,
      {
        align: "right",
      },
    );
    doc.setTextColor(0, 0, 0);
    statusLineY += 5;
  }

  if (viewDetails.reference_no) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Ref: ${viewDetails.reference_no}`,
      pageWidth - margin,
      statusLineY,
      {
        align: "right",
      },
    );
    doc.setTextColor(0, 0, 0);
  }

  y = Math.max(logoBottom, y + 22) + 4;

  const billToX = pageWidth - margin;
  const colMaxWidth = pageWidth / 2 - margin - 6;

  let companyY = y;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(viewDetails.CompanyName || "Company Name", margin, companyY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  companyY += 5;

  const writeLeftLine = (text) => {
    const lines = doc.splitTextToSize(text, colMaxWidth);
    doc.text(lines, margin, companyY);
    companyY += lines.length * 4.3;
  };

  if (viewDetails.CompanyAddress) writeLeftLine(viewDetails.CompanyAddress);

  const companyCityLine = [viewDetails.CompanyCity, viewDetails.CompanyState]
    .filter(Boolean)
    .join(" ");
  if (companyCityLine) writeLeftLine(companyCityLine);

  const companyCountryLine = [
    viewDetails.CompanyCountryCode,
    viewDetails.CompanyPostalCode,
  ]
    .filter(Boolean)
    .join(" ");
  if (companyCountryLine) writeLeftLine(companyCountryLine);

  if (viewDetails.CompanyGSTIN)
    writeLeftLine(`GSTIN: ${viewDetails.CompanyGSTIN}`);

  const companyPhones = [
    viewDetails.CompanyPhoneNumber,
    viewDetails.CompanyMobileNumber
      ? `${viewDetails.CompanyMobileNumber} (M)`
      : null,
  ]
    .filter(Boolean)
    .join(", ");
  if (companyPhones) writeLeftLine(companyPhones);

  let billY = y;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Estimate To:", billToX, billY, { align: "right" });

  billY += 5;

  doc.text(viewDetails.ClientName || "N/A", billToX, billY, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  billY += 5;

  const clientAddress = [viewDetails.billing_street, viewDetails.billing_city]
    .filter(Boolean)
    .join(", ");
  if (clientAddress) {
    const lines = doc.splitTextToSize(clientAddress, colMaxWidth);
    doc.text(lines, billToX, billY, { align: "right" });
    billY += lines.length * 4.3;
  }

  if (viewDetails.billing_state) {
    doc.text(viewDetails.billing_state, billToX, billY, { align: "right" });
    billY += 4.3;
  }

  const clientCountryLine = [
    viewDetails.billing_country_code || viewDetails.CountryName,
    viewDetails.billing_zip,
  ]
    .filter(Boolean)
    .join(" ");
  if (clientCountryLine) {
    doc.text(clientCountryLine, billToX, billY, { align: "right" });
    billY += 4.3;
  }

  const clientGSTIN = viewDetails.ClientGSTIN || viewDetails.CustomerGSTIN;
  if (clientGSTIN) {
    doc.text(`GSTIN: ${clientGSTIN}`, billToX, billY, { align: "right" });
    billY += 4.3;
  }

  billY += 4;

  doc.text(
    `Estimate Date: ${formatDate(viewDetails.date || viewDetails.datecreated)}`,
    billToX,
    billY,
    { align: "right" },
  );
  billY += 4.5;

  doc.text(
    `Expiry Date: ${formatDate(viewDetails.expirydate || viewDetails.open_till)}`,
    billToX,
    billY,
    { align: "right" },
  );
  billY += 4.5;

  y = Math.max(companyY, billY) + 8;

  const colWidths = {
    num: 8,
    item: 78,
    qty: 22,
    rate: 24,
    tax: 30,
    amount: 20,
  };

  const tableData = items.map((item, index) => {
    const qty = Number(item.qty || 0);
    const rate = Number(item.rate || 0);
    const amount = qty * rate;

    const taxLines = (item.item_taxes || [])
      .map(
        (t) =>
          `${extractTaxName(t.taxname)} ${Number(t.taxrate || 0).toFixed(2)}%`,
      )
      .join("\n");

    const itemCellText = [
      item.description || "N/A",
      item.long_description || "",
    ]
      .filter(Boolean)
      .join("\n");

    return [
      index + 1,
      itemCellText,
      `${qty}${item.unit ? ` ${item.unit}` : ""}`,
      formatMoney(rate),
      taxLines || "N/A",
      formatMoney(amount),
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [["#", "Item", "Qty", "Rate", "Tax", "Amount"]],
    body: tableData,
    theme: "plain",

    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
      valign: "top",
      overflow: "linebreak",
    },

    headStyles: {
      fillColor: [31, 41, 55],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8.5,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
    },

    columnStyles: {
      0: { cellWidth: colWidths.num, halign: "center" },
      1: { cellWidth: colWidths.item },
      2: { cellWidth: colWidths.qty, halign: "center" },
      3: { cellWidth: colWidths.rate, halign: "right" },
      4: { cellWidth: colWidths.tax },
      5: { cellWidth: colWidths.amount, halign: "right" },
    },

    margin: { left: margin, right: margin },

    didDrawCell: (data) => {
      if (data.section !== "body" || data.column.index !== 1) return;

      const item = items[data.row.index];
      if (!item) return;

      doc.setFillColor(255, 255, 255);
      doc.rect(
        data.cell.x,
        data.cell.y,
        data.cell.width,
        data.cell.height,
        "F",
      );

      const textX = data.cell.x + 2.5;
      let textY = data.cell.y + 4.5;
      const maxWidth = data.cell.width - 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      const titleLines = doc.splitTextToSize(
        item.description || "N/A",
        maxWidth,
      );
      doc.text(titleLines, textX, textY);
      textY += titleLines.length * 3.7;

      if (item.long_description) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);

        item.long_description.split("\n").forEach((line) => {
          const wrapped = doc.splitTextToSize(line, maxWidth);
          doc.text(wrapped, textX, textY);
          textY += wrapped.length * 3.5;
        });
      }
    },
  });

  const totalsWidth = 75;
  const totalsLeft = pageWidth - margin - totalsWidth;

  let totalsY = doc.lastAutoTable.finalY + 6;

  if (totalsY > pageHeight - 70) {
    doc.addPage();
    totalsY = 20;
  }

  const subtotal = Number(viewDetails.subtotal || 0);
  const discountPercent = Number(viewDetails.discount_percent || 0);
  const discountTotal = Number(viewDetails.discount_total || 0);

  const taxTotals = new Map();
  items.forEach((item) => {
    const amount = Number(item.qty || 0) * Number(item.rate || 0);
    (item.item_taxes || []).forEach((t) => {
      const name = extractTaxName(t.taxname);
      const rate = Number(t.taxrate || 0);
      const key = `${name}_${rate}`;
      const taxAmount = (amount * rate) / 100;
      taxTotals.set(key, {
        name,
        rate,
        amount: (taxTotals.get(key)?.amount || 0) + taxAmount,
      });
    });
  });

  const totalsRows = [{ label: "Sub Total", value: rs(subtotal) }];

  if (discountPercent || discountTotal) {
    totalsRows.push({
      label: discountPercent ? `Discount (${discountPercent}%)` : "Discount",
      value: `- ${rs(discountTotal)}`,
    });
  }

  Array.from(taxTotals.values()).forEach((t) => {
    totalsRows.push({
      label: `${t.name} (${t.rate.toFixed(2)}%)`,
      value: rs(t.amount),
    });
  });

  const adjustment = Number(viewDetails.adjustment || 0);
  if (adjustment) {
    totalsRows.push({ label: "Adjustment", value: rs(adjustment) });
  }

  totalsRows.push({
    label: "Total",
    value: rs(viewDetails.total),
    shaded: true,
    bold: true,
  });

  const rowHeight = 6.5;

  totalsRows.forEach((row) => {
    if (row.shaded) {
      doc.setFillColor(237, 237, 237);
      doc.rect(totalsLeft, totalsY - 4.5, totalsWidth, rowHeight, "F");
    }

    doc.setFont("helvetica", row.bold ? "bold" : "normal");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    doc.text(row.label, totalsLeft + 2, totalsY);
    doc.text(row.value, pageWidth - margin - 2, totalsY, { align: "right" });

    totalsY += rowHeight;
  });

  let afterTotalsY = totalsY + 6;

  if (afterTotalsY > pageHeight - 40) {
    doc.addPage();
    afterTotalsY = 20;
  }

  if (
    typeof viewDetails.clientnote === "string" &&
    viewDetails.clientnote.trim()
  ) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Note:", margin, afterTotalsY);

    afterTotalsY += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);

    const noteLines = doc.splitTextToSize(viewDetails.clientnote, usableWidth);
    doc.text(noteLines, margin, afterTotalsY);

    afterTotalsY += noteLines.length * 4.3 + 6;
  }

  if (typeof viewDetails.terms === "string" && viewDetails.terms.trim()) {
    if (afterTotalsY > pageHeight - 30) {
      doc.addPage();
      afterTotalsY = 20;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Terms & Conditions:", margin, afterTotalsY);

    afterTotalsY += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    viewDetails.terms
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .forEach((line) => {
        const wrapped = doc.splitTextToSize(line, usableWidth);
        doc.text(wrapped, margin, afterTotalsY);
        afterTotalsY += wrapped.length * 4 + 1.5;
      });
  }

  afterTotalsY += 10;

  if (afterTotalsY > pageHeight - 30) {
    doc.addPage();
    afterTotalsY = 30;
  }

  const acceptedName = [
    viewDetails.acceptance_firstname,
    viewDetails.acceptance_lastname,
  ]
    .filter(Boolean)
    .join(" ");

  if (viewDetails.acceptance_date && acceptedName) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Accepted By:", margin, afterTotalsY);

    doc.setFont("helvetica", "normal");
    doc.text(
      `${acceptedName}  |  ${formatDate(viewDetails.acceptance_date)}`,
      margin + 28,
      afterTotalsY,
    );

    afterTotalsY += 10;
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      "Client Signature _________________________",
      margin,
      afterTotalsY,
    );

    doc.text(
      "Authorized Signature _________________________",
      pageWidth - margin,
      afterTotalsY,
      { align: "right" },
    );
  }

  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);

    doc.text(viewDetails.CompanyName || "", margin, pageHeight - 8);

    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 8, {
      align: "right",
    });
  }

  // doc.save(`${estimateNumber}.pdf`);
  return doc;
};

const getCreditNoteStatusColor = (status) => {
  const normalized = (status || "").toLowerCase();

  if (normalized === "closed") return [22, 163, 74];
  if (normalized === "open") return [37, 99, 235];
  if (normalized === "void") return [150, 150, 150];
  return [100, 100, 100];
};

export const generateCreditNotePDF = (viewDetails) => {
  if (!viewDetails) return;

  const doc = new jsPDF("p", "mm", "a4");

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const margin = 14;
  const usableWidth = pageWidth - margin * 2;

  const items = viewDetails.items || viewDetails.Itemable || [];
  const getItemTaxes = (item) => item.item_taxes || item.taxes || [];

  const creditNoteNumber =
    (viewDetails.PrefixNumber || "").replace(/\s+/g, "") ||
    `CN-${viewDetails.id}`;

  let y = 16;
  let logoBottom = y;

  const logoWidth = 42;
  const logoHeight = 14;
  doc.addImage(logo, margin, y, logoWidth, logoHeight);
  logoBottom = y + logoHeight;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("CREDIT NOTE", pageWidth - margin, y + 6, { align: "right" });

  doc.setFontSize(10);
  doc.text(`# ${creditNoteNumber}`, pageWidth - margin, y + 12, {
    align: "right",
  });

  let statusLineY = y + 18;

  if (viewDetails.StatusName) {
    const [r, g, b] = getCreditNoteStatusColor(viewDetails.StatusName);
    doc.setTextColor(r, g, b);
    doc.setFontSize(10);
    doc.text(
      viewDetails.StatusName.toUpperCase(),
      pageWidth - margin,
      statusLineY,
      {
        align: "right",
      },
    );
    doc.setTextColor(0, 0, 0);
    statusLineY += 5;
  }

  if (viewDetails.reference_no) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Ref: ${viewDetails.reference_no}`,
      pageWidth - margin,
      statusLineY,
      {
        align: "right",
      },
    );
    doc.setTextColor(0, 0, 0);
  }

  y = Math.max(logoBottom, y + 22) + 4;

  const billToX = pageWidth - margin;
  const colMaxWidth = pageWidth / 2 - margin - 6;

  let companyY = y;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(viewDetails.CompanyName || "Company Name", margin, companyY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  companyY += 5;

  const writeLeftLine = (text) => {
    const lines = doc.splitTextToSize(text, colMaxWidth);
    doc.text(lines, margin, companyY);
    companyY += lines.length * 4.3;
  };

  if (viewDetails.CompanyAddress) writeLeftLine(viewDetails.CompanyAddress);

  const companyCityLine = [viewDetails.CompanyCity, viewDetails.CompanyState]
    .filter(Boolean)
    .join(" ");
  if (companyCityLine) writeLeftLine(companyCityLine);

  const companyCountryLine = [
    viewDetails.CompanyCountryCode,
    viewDetails.CompanyPostalCode,
  ]
    .filter(Boolean)
    .join(" ");
  if (companyCountryLine) writeLeftLine(companyCountryLine);

  if (viewDetails.CompanyGSTIN)
    writeLeftLine(`GSTIN: ${viewDetails.CompanyGSTIN}`);

  const companyPhones = [
    viewDetails.CompanyPhoneNumber,
    viewDetails.CompanyMobileNumber
      ? `${viewDetails.CompanyMobileNumber} (M)`
      : null,
  ]
    .filter(Boolean)
    .join(", ");
  if (companyPhones) writeLeftLine(companyPhones);

  let billY = y;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Credit Note To:", billToX, billY, { align: "right" });

  billY += 5;

  doc.text(viewDetails.ClientName || "N/A", billToX, billY, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  billY += 5;

  const clientAddress = [viewDetails.billing_street, viewDetails.billing_city]
    .filter(Boolean)
    .join(", ");
  if (clientAddress) {
    const lines = doc.splitTextToSize(clientAddress, colMaxWidth);
    doc.text(lines, billToX, billY, { align: "right" });
    billY += lines.length * 4.3;
  }

  if (viewDetails.billing_state) {
    doc.text(viewDetails.billing_state, billToX, billY, { align: "right" });
    billY += 4.3;
  }

  const clientCountryLine = [
    viewDetails.billing_country_code || viewDetails.CountryName,
    viewDetails.billing_zip,
  ]
    .filter(Boolean)
    .join(" ");
  if (clientCountryLine) {
    doc.text(clientCountryLine, billToX, billY, { align: "right" });
    billY += 4.3;
  }

  const clientGSTIN = viewDetails.ClientGSTIN || viewDetails.CustomerGSTIN;
  if (clientGSTIN) {
    doc.text(`GSTIN: ${clientGSTIN}`, billToX, billY, { align: "right" });
    billY += 4.3;
  }

  billY += 4;

  doc.text(
    `Credit Note Date: ${formatDate(viewDetails.datecreated)}`,
    billToX,
    billY,
    { align: "right" },
  );
  billY += 4.5;

  y = Math.max(companyY, billY) + 8;

  const colWidths = {
    num: 8,
    item: 78,
    qty: 22,
    rate: 24,
    tax: 30,
    amount: 20,
  };

  const tableData = items.map((item, index) => {
    const qty = Number(item.qty || 0);
    const rate = Number(item.rate || 0);
    const amount = qty * rate;

    const taxLines = getItemTaxes(item)
      .map(
        (t) =>
          `${extractTaxName(t.taxname)} ${Number(t.taxrate || 0).toFixed(2)}%`,
      )
      .join("\n");

    const itemCellText = [
      item.description || "N/A",
      item.long_description || "",
    ]
      .filter(Boolean)
      .join("\n");

    return [
      index + 1,
      itemCellText,
      `${qty}${item.unit ? ` ${item.unit}` : ""}`,
      formatMoney(rate),
      taxLines || "N/A",
      formatMoney(amount),
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [["#", "Item", "Qty", "Rate", "Tax", "Amount"]],
    body: tableData,
    theme: "plain",

    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
      valign: "top",
      overflow: "linebreak",
    },

    headStyles: {
      fillColor: [31, 41, 55],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8.5,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
    },

    columnStyles: {
      0: { cellWidth: colWidths.num, halign: "center" },
      1: { cellWidth: colWidths.item },
      2: { cellWidth: colWidths.qty, halign: "center" },
      3: { cellWidth: colWidths.rate, halign: "right" },
      4: { cellWidth: colWidths.tax },
      5: { cellWidth: colWidths.amount, halign: "right" },
    },

    margin: { left: margin, right: margin },

    didDrawCell: (data) => {
      if (data.section !== "body" || data.column.index !== 1) return;

      const item = items[data.row.index];
      if (!item) return;

      doc.setFillColor(255, 255, 255);
      doc.rect(
        data.cell.x,
        data.cell.y,
        data.cell.width,
        data.cell.height,
        "F",
      );

      const textX = data.cell.x + 2.5;
      let textY = data.cell.y + 4.5;
      const maxWidth = data.cell.width - 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      const titleLines = doc.splitTextToSize(
        item.description || "N/A",
        maxWidth,
      );
      doc.text(titleLines, textX, textY);
      textY += titleLines.length * 3.7;

      if (item.long_description) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);

        item.long_description.split("\n").forEach((line) => {
          const wrapped = doc.splitTextToSize(line, maxWidth);
          doc.text(wrapped, textX, textY);
          textY += wrapped.length * 3.5;
        });
      }
    },
  });

  const totalsWidth = 75;
  const totalsLeft = pageWidth - margin - totalsWidth;

  let totalsY = doc.lastAutoTable.finalY + 6;

  if (totalsY > pageHeight - 70) {
    doc.addPage();
    totalsY = 20;
  }

  const subtotal = Number(viewDetails.subtotal || 0);
  const discountPercent = Number(viewDetails.discount_percent || 0);
  const discountTotal = Number(viewDetails.discount_total || 0);

  const taxTotals = new Map();
  items.forEach((item) => {
    const amount = Number(item.qty || 0) * Number(item.rate || 0);
    getItemTaxes(item).forEach((t) => {
      const name = extractTaxName(t.taxname);
      const rate = Number(t.taxrate || 0);
      const key = `${name}_${rate}`;
      const taxAmount = (amount * rate) / 100;
      taxTotals.set(key, {
        name,
        rate,
        amount: (taxTotals.get(key)?.amount || 0) + taxAmount,
      });
    });
  });

  const totalsRows = [{ label: "Sub Total", value: rs(subtotal) }];

  if (discountPercent || discountTotal) {
    totalsRows.push({
      label: discountPercent ? `Discount (${discountPercent}%)` : "Discount",
      value: `- ${rs(discountTotal)}`,
    });
  }

  Array.from(taxTotals.values()).forEach((t) => {
    totalsRows.push({
      label: `${t.name} (${t.rate.toFixed(2)}%)`,
      value: rs(t.amount),
    });
  });

  const adjustment = Number(viewDetails.adjustment || 0);
  if (adjustment) {
    totalsRows.push({ label: "Adjustment", value: rs(adjustment) });
  }

  totalsRows.push({
    label: "Total",
    value: rs(viewDetails.total),
    shaded: true,
    bold: true,
  });

  if (viewDetails.remaining_amount !== undefined) {
    totalsRows.push({
      label: "Remaining Credit",
      value: rs(viewDetails.remaining_amount),
      shaded: true,
      bold: true,
    });
  }

  const rowHeight = 6.5;

  totalsRows.forEach((row) => {
    if (row.shaded) {
      doc.setFillColor(237, 237, 237);
      doc.rect(totalsLeft, totalsY - 4.5, totalsWidth, rowHeight, "F");
    }

    doc.setFont("helvetica", row.bold ? "bold" : "normal");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    doc.text(row.label, totalsLeft + 2, totalsY);
    doc.text(row.value, pageWidth - margin - 2, totalsY, { align: "right" });

    totalsY += rowHeight;
  });

  let afterTotalsY = totalsY + 6;

  if (afterTotalsY > pageHeight - 40) {
    doc.addPage();
    afterTotalsY = 20;
  }

  if (
    typeof viewDetails.clientnote === "string" &&
    viewDetails.clientnote.trim()
  ) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Note:", margin, afterTotalsY);

    afterTotalsY += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);

    const noteLines = doc.splitTextToSize(viewDetails.clientnote, usableWidth);
    doc.text(noteLines, margin, afterTotalsY);

    afterTotalsY += noteLines.length * 4.3 + 6;
  }

  if (typeof viewDetails.terms === "string" && viewDetails.terms.trim()) {
    if (afterTotalsY > pageHeight - 30) {
      doc.addPage();
      afterTotalsY = 20;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Terms & Conditions:", margin, afterTotalsY);

    afterTotalsY += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    viewDetails.terms
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .forEach((line) => {
        const wrapped = doc.splitTextToSize(line, usableWidth);
        doc.text(wrapped, margin, afterTotalsY);
        afterTotalsY += wrapped.length * 4 + 1.5;
      });
  }

  afterTotalsY += 10;

  if (afterTotalsY > pageHeight - 20) {
    doc.addPage();
    afterTotalsY = 30;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(
    "Authorized Signature _________________________",
    margin,
    afterTotalsY,
  );

  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);

    doc.text(viewDetails.CompanyName || "", margin, pageHeight - 8);

    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 8, {
      align: "right",
    });
  }

  // doc.save(`${creditNoteNumber}.pdf`);
  return doc;
};

const View = () => {
  const { type } = useParams();
  const { state } = useLocation();
  const record = state?.record;

  const id = record?.id;

  const navigate = useNavigate();

  const [viewDetails, setViewDetails] = useState({});
  const [loading, setLoading] = useState(false);

  const getViewById = async (id) => {
    try {
      setLoading(true);

      let endpoint = "";

      switch (type) {
        case "proposal":
          endpoint = `proposal/GetProposal/${id}`;
          break;

        case "estimate":
          endpoint = `estimates/GetEstimate/${id}`;
          break;

        case "invoice":
          endpoint = `invoice/GetInvoice/${id}`;
          break;

        case "creditnote":
          endpoint = `creditnotes/GetCreditNote/${id}`;
          break;

        default:
          console.error("Invalid type:", type);
          return;
      }

      const response = await fetch(`${BASE_CRM_URL}${endpoint}`);

      const data = await response.json();

      setViewDetails(data.ResultData);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getViewById(id);
    console.log("view by id is triggered ", id);
  }, [id, type]);

  const [activeTab, setActiveTab] = useState(type);
  useEffect(() => {
    setActiveTab(type);
  }, [type]);

  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);
  const [reminderDrawerOpen, setReminderDrawerOpen] = useState(false);
  const [activelogDrawerOpen, setActiveLogDrawerOpen] = useState(false);

  const formattedType = type
    ? type.charAt(0).toUpperCase() + type.slice(1)
    : "";

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "draft":
        return {
          backgroundColor: "#E7F1FF",
          color: "#0D6EFD",
        };

      case "sent":
        return {
          backgroundColor: "#E8F4FD",
          color: "#0D6EFD",
        };

      case "open":
        return {
          backgroundColor: "#FFF4E5",
          color: "#FD7E14",
        };

      case "revised":
        return {
          backgroundColor: "#F0E7FF",
          color: "#6F42C1",
        };

      case "declined":
        return {
          backgroundColor: "#FCE7E7",
          color: "#DC3545",
        };

      case "accepted":
        return {
          backgroundColor: "#E8F8F0",
          color: "#198754",
        };

      case "expired":
        return {
          backgroundColor: "#F1F1F1",
          color: "#6C757D",
        };

      case "Close":
        return {
          backgroundColor: "#E9ECEF",
          color: "#495057",
        };

      case "paid":
        return {
          backgroundColor: "#E8F8F0",
          color: "#198754",
        };

      case "unpaid":
        return {
          backgroundColor: "#E6F7FF",
          color: "#0D6EFD",
        };

      case "partially paid":
      case "partialypaid":
        return {
          backgroundColor: "#FFF4E5",
          color: "#FD7E14",
        };

      case "cancelled":
        return {
          backgroundColor: "#FCE7E7",
          color: "#DC3545",
        };

      default:
        return {
          backgroundColor: "#F1F1F1",
          color: "#6C757D",
        };
    }
  };

  const backPaths = {
    estimate: "/crm/estimates",
    invoice: "/crm/invoices",
    creditnote: "/crm/creditnotes",
    proposal: "/crm/proposals",
  };

  const handleConvert = (targetType) => {
    navigate(`/crm/add/${targetType}?convertFrom=${type}&convertId=${id}`);
  };

  const buildPDFForType = () => {
    switch (type) {
      case "proposal":
        return generateProposalPDF(viewDetails);

      case "estimate":
        return generateEstimatePDF(viewDetails);

      case "invoice":
        return generateInvoicePDF(viewDetails);

      case "creditnote":
        return generateCreditNotePDF(viewDetails);

      default:
        console.error("Invalid PDF type:", type);
        return null;
    }
  };

  const handleDownloadPDF = () => {
    const doc = buildPDFForType();
    if (!doc) return;

    const fileNumber =
      viewDetails?.PrefixNumber ||
      `${type.toUpperCase()}-${viewDetails?.id || id}`;

    doc.save(`${fileNumber}.pdf`);
  };

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [subject, setSubject] = useState("");
  const [messageHtml, setMessageHtml] = useState(null); // null = template not loaded yet
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [sending, setSending] = useState(false);

  const documentTypeMap = {
    proposal: "proposal",
    estimate: "estimate",
    invoice: "invoice",
    creditnote: "credit_note",
  };

  const slugForType = (documentType) => {
    if (documentType === "credit_note") {
      return "credit-note-send-to-client";
    }

    if (documentType === "proposal") {
      return "proposal-send-to-customer";
    }

    return `${documentType}-send-to-client`;
  };

  useEffect(() => {
    const documentType = documentTypeMap[type];
    if (!emailModalOpen || !documentType) return;

    let cancelled = false;
    setLoadingTemplate(true);
    setMessageHtml(null);

    const slug = slugForType(documentType);

    fetch(`${BASE_CRM_URL}Email/GetEmailTemplate?slug=${slug}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (!data?.Status) {
          throw new Error(data?.Message || "Failed to load email template");
        }
        setSubject(data.ResultData?.subject || "");
        setMessageHtml(data.ResultData?.message || "");
      })
      .catch((err) => {
        if (cancelled) return;
        message.error(err.message || "Failed to load email template");
        setMessageHtml("");
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplate(false);
      });

    return () => {
      cancelled = true;
    };
  }, [emailModalOpen, type]);

  const handleSendEmail = async () => {
    if (!emailInput) {
      message.error("Please enter an email address");
      return;
    }
    if (!subject.trim()) {
      message.error("Subject cannot be empty");
      return;
    }

    const doc = buildPDFForType();
    if (!doc) return;

    const documentType = documentTypeMap[type];
    if (!documentType) {
      console.error("Invalid document type:", type);
      return;
    }

    const pdfBlob = doc.output("blob");
    const fileNumber =
      viewDetails?.PrefixNumber || `${documentType}-${viewDetails?.id || id}`;

    const formData = new FormData();
    formData.append("pdf", pdfBlob, `${fileNumber}.pdf`);
    formData.append("email", emailInput);
    formData.append("subject", subject);
    formData.append("message", messageHtml || "");
    formData.append("documentType", documentType);

    try {
      setSending(true);
      const response = await fetch(`${BASE_CRM_URL}Email/SendDocumentEmail`, {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || !result.Status) {
        throw new Error(result.Message || "Failed to send email");
      }
      message.success(result.Message || "Email sent successfully");
      setEmailModalOpen(false);
      setEmailInput("");
      setSubject("");
      setMessageHtml(null);
    } catch (err) {
      console.error(err);
      message.error(err.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState();

  // const [msgtype, setMsgType] = useState(type);

  const handleSendWhatsApp = async () => {
    if (!phoneNumber) {
      message.error("Please enter an WhatsApp ");
      return;
    }
    // if (!subject.trim()) {
    //   message.error("Number cannot be empty");
    //   return;
    // }

    const doc = buildPDFForType();
    if (!doc) return;

    const documentType = documentTypeMap[type];
    if (!documentType) {
      console.error("Invalid document type:", type);
      return;
    }

    const pdfBlob = doc.output("blob");
    const fileNumber =
      viewDetails?.PrefixNumber || `${documentType}-${viewDetails?.id || id}`;

    const formData = new FormData();
    formData.append("name", viewDetails?.ClientName);
    formData.append("file", pdfBlob, `${fileNumber}.pdf`);
    formData.append("to", phoneNumber);
    formData.append("number", `${type} # ${viewDetails?.PrefixNumber}`);

    try {
      setSending(true);
      const response = await fetch(
        `${BASE_CRM_URL}WhatsApp/SendDocumentWhatsApp`,
        {
          method: "POST",
          body: formData,
        },
      );
      const result = await response.json();
      if (!response.ok || !result.status) {
        throw new Error(result.Message || "Failed to send WhatsApp");
      }
      message.success(result.Message || "WhatsApp sent successfully");
      setWhatsAppModalOpen(false);
      setPhoneNumber();
    } catch (err) {
      console.error(err);
      message.error(err.message || "Failed to send WhatsApp");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page-wrapper">
      <Spin spinning={loading} tip="Loading..." size="large">
        <div className="content pt-2">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb d-flex justify-content-between w-100">
              <div className="d-flex align-items-center">
                <li className="breadcrumb-item">
                  <Link to="/crm/dashboard">
                    <i className="fas fa-home fa-lg" />
                  </Link>
                </li>
                <h4 className="breadcrumb-item mb-0" aria-current="page">
                  View {formattedType}
                </h4>
              </div>

              <ul
                className="nav nav-tabs nav-tabs-bottom"
                id="myTab"
                role="tablist"
              >
                {/* Dynamic Main Tab */}
                <li className="nav-item" role="presentation">
                  <Link
                    className={`nav-link ${activeTab === type ? "active" : ""}`}
                    id={`${type}-tab`}
                    data-bs-toggle="tab"
                    data-bs-target={`#${type}-tab-pane`}
                    type="button"
                    role="tab"
                    aria-controls={`${type}-tab-pane`}
                    aria-selected={activeTab === type}
                    onClick={() => setActiveTab(type)}
                  >
                    {formattedType}
                  </Link>
                </li>

                {/* Tasks */}
                {type !== "creditnote" && (
                  <li className="nav-item" role="presentation">
                    <Link
                      className={`nav-link ${
                        activeTab === "tasks" ? "active" : ""
                      }`}
                      id="tasks-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tasks-tab-pane"
                      type="button"
                      role="tab"
                      aria-controls="tasks-tab-pane"
                      aria-selected={activeTab === "tasks"}
                      onClick={() => setActiveTab("tasks")}
                    >
                      Tasks
                    </Link>
                  </li>
                )}
                {/* Reminders */}
                <li className="nav-item" role="presentation">
                  <Link
                    to="#"
                    className={`nav-link ${
                      activeTab === "reminders" ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      setReminderDrawerOpen(true);
                    }}
                  >
                    Reminders
                  </Link>
                </li>

                {/* Notes */}
                <li className="nav-item" role="presentation">
                  <Link
                    to="#"
                    className={`nav-link ${
                      activeTab === "notes" ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      setNoteDrawerOpen(true);
                    }}
                  >
                    Notes
                  </Link>
                </li>
                <li className="nav-item d-none" role="presentation">
                  <Link
                    to="#"
                    className={`nav-link ${
                      activeTab === "activelog" ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("activelog");
                    }}
                  >
                    ActiveLog
                  </Link>
                </li>
              </ul>

              <div className="page-btn d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Left Side */}
                <a onClick={handleDownloadPDF}>
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                </a>
                <a
                  onClick={() => setEmailModalOpen(true)}
                  title="Send via Email"
                >
                  <i className="fa-solid text-info fa-envelope fa-lg" />
                </a>
                <a
                  onClick={() => setWhatsAppModalOpen(true)}
                  title="Send via whatsApp"
                >
                  <i className="fa-brands text-success fa-whatsapp fa-lg" />
                </a>
                <span
                  className="badge px-3 py-2"
                  style={getStatusStyle(viewDetails?.StatusName)}
                >
                  {viewDetails?.StatusName || viewDetails?.InvoiceStatus || "-"}
                </span>

                <div className="d-flex align-items-center gap-2">
                  <div className="dropdown">
                    <button
                      type="button"
                      className="btn btn-soft-danger btn-sm dropdown-toggle"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      Convert
                    </button>

                    <ul className="dropdown-menu dropdown-menu-end">
                      {type === "proposal" &&
                        record?.rel_type === "customer" && (
                          <>
                            <li>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => handleConvert("estimate")}
                              >
                                Convert to Estimate
                              </button>
                            </li>

                            <li>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => handleConvert("invoice")}
                              >
                                Convert to Invoice
                              </button>
                            </li>
                          </>
                        )}

                      {type === "estimate" && (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => handleConvert("invoice")}
                          >
                            Convert to Invoice
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>

                  <Link
                    to={backPaths[type] || "/crm/dashboard"}
                    className="btn btn-dark btn-sm"
                  >
                    <i className="ti ti-arrow-left"></i>
                  </Link>
                </div>
              </div>
            </ol>
          </nav>

          <div className="container-fluid">
            <div className="card shadow proposal-summary">
              <div className="card-body pb-1 ">
                <div className="row align-items-center">
                  <div className="col-md-3 col-sm-6 mb-2 border-end text-center">
                    <div className="d-inline-flex align-items-center">
                      <span className="fw-bold me-2">{formattedType} No:</span>
                      <span className="fw-bold text-danger">
                        {viewDetails?.PrefixNumber || "-"}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-3 col-sm-6 mb-2 border-end text-center">
                    <div className="d-inline-flex align-items-center">
                      <i className="fa-regular fa-calendar me-2"></i>
                      <span className="fw-bold me-2">Issued On:</span>
                      <span className="fw-bold">
                        {dayjs(viewDetails?.datecreated || "-").format(
                          "DD-MM-YYYY",
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-3 col-sm-6 mb-2 border-end text-center">
                    <div className="d-inline-flex align-items-center">
                      <i className="fa-regular fa-clock  me-2"></i>
                      <span className="fw-bold me-2">Valid Until:</span>
                      <span className="fw-bold">
                        {dayjs(viewDetails?.open_till || "-").format(
                          "DD-MM-YYYY",
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-3 col-sm-6 mb-2 text-center">
                    <div className="d-inline-flex align-items-center">
                      <i className="fa-solid fa-indian-rupee-sign me-2"></i>
                      <span className="fw-bold me-2">Total:</span>
                      <span className="fw-bold text-success">
                        {Number(viewDetails?.total || "-").toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card shadow">
              <div className="card-body pt-0">
                <div className="tab-content " id="myTabContent">
                  <div
                    className={`tab-pane fade  ${
                      activeTab === type ? "show active" : ""
                    }`}
                    id="proposal-tab-pane"
                    role="tabpanel"
                    aria-labelledby="proposal-tab"
                  >
                    <div className="row">
                      <div className="col-12">
                        <div className="card-body">
                          <div className="row">
                            {/* Company Info */}
                            <div className="col-5">
                              <h6 className=" fw-bold mb-3">
                                <i className="fa-solid fa-building me-2"></i>

                                {viewDetails?.CompanyName || "-"}
                              </h6>

                              {/* <p className="mb-1">
                                <i className="fa-solid fa-location-dot  me-2"></i>
                               
                              </p> */}

                              <p className="mb-1">
                                <i className="fa-solid fa-map-location-dot  me-2"></i>
                                {viewDetails?.CompanyAddress || "-"},{" "}
                                {viewDetails?.CompanyCity || "-"},{" "}
                                {viewDetails?.CompanyCountryCode || "-"} -{" "}
                                {viewDetails?.CompanyPostalCode || "-"}
                              </p>

                              {/* <p className="mb-1">
                                <i className="fa-solid fa-earth-asia  me-2"></i>
                               
                              </p> */}

                              <p className="mb-1">
                                <i className="fa-solid fa-file-invoice  me-2"></i>
                                GSTIN: {viewDetails?.CompanyGSTIN || "-"}
                              </p>

                              <p className="mb-0">
                                <i className="fa-solid fa-phone-volume  me-2"></i>
                                {viewDetails?.CompanyPhoneNumber || "-"},{" "}
                              </p>
                            </div>

                            <div className="col-3 d-flex justify-content-start align-items-end">
                              <div className="text-start">
                                <p className="mb-0">
                                  <i className="fa-solid fa-user-check me-2"></i>
                                  <strong>Assigned:</strong>{" "}
                                  {viewDetails?.AddedByName || "-"}
                                </p>
                              </div>
                            </div>

                            {/* Proposal Information */}
                            <div className="col-4 d-flex justify-content-end">
                              <div className="text-end">
                                <h6 className=" fw-bold mb-3">
                                  {formattedType} To
                                </h6>

                                <p className="mb-2 fw-semibold">
                                  <i className="fa-solid fa-user-tie  me-2"></i>
                                  {viewDetails?.ClientName || "-"}
                                </p>

                                {/* <p className="mb-2">
                                  <i className="fa-solid fa-location-dot  me-2"></i>
                                  {viewDetails?.address}
                                </p> */}

                                <p className="mb-2">
                                  <i className="fa-solid fa-city  me-2"></i>
                                  {viewDetails?.billing_street || "-"},{" "}
                                  {viewDetails?.billing_city || "-"},{" "}
                                  {viewDetails?.billing_state || "-"} -{" "}
                                  {viewDetails?.billing_zip || "-"}
                                </p>

                                <p className="mb-2">
                                  <i className="fa-solid fa-phone  me-2"></i>
                                  {viewDetails?.PhoneNumber || "-"},{" "}
                                  <i className="fa-solid fa-envelope  me-2"></i>
                                  {viewDetails?.MailId || "-"}
                                </p>

                                {/* <p className="mb-0">
                                  <i className="fa-solid fa-envelope  me-2"></i>
                                  {viewDetails?.email}
                                </p> */}
                              </div>
                            </div>

                            {/* Summary Card */}
                            <div className="card border-0 shadow-sm mt-4 d-none">
                              <div className="card-body">
                                <h4 className="text-primary fw-bold mb-3">
                                  <i className="fa-solid fa-indian-rupee-sign me-2"></i>
                                  {Number().toLocaleString().viewDetails?.total}
                                </h4>

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <span>
                                    <i className="fa-solid fa-circle-check text-success me-2"></i>
                                    Status
                                  </span>

                                  <span className="badge bg-soft-info px-3 py-2">
                                    {viewDetails?.status || "-"}
                                  </span>
                                </div>

                                <hr />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Table on the left */}
                      <div className="col-12">
                        <div className="card p-3 mb-3">
                          <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0">
                              <thead className="table-primary">
                                <tr>
                                  <th style={{ width: 60 }}>#</th>
                                  <th>Item</th>

                                  <th className="text-center">Qty</th>
                                  <th className="text-end">Rate</th>
                                  <th className="text-end">Tax</th>
                                  <th className="text-end">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {viewDetails?.Itemable?.map((item, index) => {
                                  const amount =
                                    Number(item.qty) * Number(item.rate);

                                  return (
                                    <tr key={item.id}>
                                      <td className="fw-semibold">
                                        {index + 1}
                                      </td>

                                      <td
                                        style={{
                                          maxWidth: "300px",
                                          whiteSpace: "normal",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        <div className="fw-semibold text-dark">
                                          {item.description}
                                        </div>
                                      </td>

                                      <td className="text-center">
                                        <span className="badge bg-light text-dark border">
                                          {item.qty}
                                        </span>
                                      </td>

                                      <td className="text-end">
                                        ₹ {Number(item.rate).toLocaleString()}
                                      </td>

                                      <td className="text-end">
                                        ₹{" "}
                                        {(
                                          Number(viewDetails?.total_tax) /
                                          viewDetails?.Itemable.length
                                        ).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </td>

                                      <td className="text-end fw-bold text-success">
                                        ₹ {amount.toLocaleString()}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-8">
                          {type !== "proposal" && (
                            <>
                              <label className="form-label fw-bold">
                                Terms & Conditions
                              </label>

                              <ol className="mb-0 ps-3">
                                {viewDetails?.terms
                                  ?.split("\n")
                                  .filter((term) => term.trim())
                                  .map((term, index) => (
                                    <li key={index} className="mb-2">
                                      {term.replace(/^\d+\)\s*/, "")}
                                    </li>
                                  ))}
                              </ol>
                            </>
                          )}
                        </div>

                        <div className="col-md-4 d-flex justify-content-end">
                          <table
                            className="table text-end"
                            style={{ height: 200 }}
                          >
                            <tbody>
                              <tr>
                                <td>Sub Total</td>
                                <td>
                                  ₹{" "}
                                  {Number(
                                    viewDetails?.subtotal,
                                  ).toLocaleString()}
                                </td>
                              </tr>

                              <tr>
                                <td>Total Tax</td>
                                <td>
                                  ₹{" "}
                                  {Number(
                                    viewDetails?.total_tax,
                                  ).toLocaleString()}
                                </td>
                              </tr>

                              <tr>
                                <td>
                                  Discount/{viewDetails?.discount_type_name}
                                </td>
                                <td>
                                  ₹{" "}
                                  {Number(
                                    viewDetails?.discount_total,
                                  ).toLocaleString()}
                                </td>
                              </tr>

                              <tr>
                                <td>Adjustment</td>
                                <td>
                                  ₹{" "}
                                  {Number(
                                    viewDetails?.adjustment,
                                  ).toLocaleString()}
                                </td>
                              </tr>

                              <tr className="table-primary bg-light">
                                <td>Total</td>
                                <td>
                                  ₹{" "}
                                  {Number(viewDetails?.total).toLocaleString()}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`tab-pane fade ${
                      activeTab === "tasks" ? "show active" : ""
                    }`}
                    id="tasks-tab-pane"
                    role="tabpanel"
                    aria-labelledby="tasks-tab"
                  >
                    <Tasks recordId={id} type={type} />
                  </div>

                  <div
                    className={`tab-pane fade  ${
                      activeTab === "activelog" ? "show active" : ""
                    }`}
                    id="activelog-tab-pane"
                    role="tabpanel"
                    aria-labelledby="activelog-tab"
                  >
                    <ActiveLog recordId={id} type={type} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <CommonFooter />

          <Notes
            recordId={id}
            type={type}
            open={noteDrawerOpen}
            onClose={() => setNoteDrawerOpen(false)}
          />
          <Reminders
            recordId={id}
            type={type}
            open={reminderDrawerOpen}
            onClose={() => setReminderDrawerOpen(false)}
          />

          <ActiveLog
            recordId={id}
            type={type}
            open={activelogDrawerOpen}
            onClose={() => setActiveLogDrawerOpen(false)}
          />
        </div>
        <Modal
          title="Send PDF via Email"
          open={emailModalOpen}
          onOk={handleSendEmail}
          confirmLoading={sending}
          onCancel={() => setEmailModalOpen(false)}
          okText="Send"
          okButtonProps={{ disabled: loadingTemplate || messageHtml === null }}
          width={720}
          destroyOnClose
        >
          <div style={{ marginBottom: 12 }}>
            <Input
              type="email"
              placeholder="Enter recipient email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <Input
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={loadingTemplate}
            />
          </div>

          {loadingTemplate || messageHtml === null ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Spin tip="Loading template..." />
            </div>
          ) : (
            <QuillEditor
              key={emailModalOpen ? "open" : "closed"}
              initialValue={messageHtml}
              onChange={setMessageHtml}
            />
          )}
        </Modal>
        <Modal
          title="Send PDF via WhatsApp"
          open={whatsAppModalOpen}
          onOk={handleSendWhatsApp}
          confirmLoading={sending}
          onCancel={() => setWhatsAppModalOpen(false)}
          okText="Send"
          okButtonProps={{ disabled: loadingTemplate }}
          width={500}
          destroyOnClose
        >
          <div style={{ marginBottom: 12 }}>
            <Input
              type="number"
              placeholder="Enter recipient number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>

          {/* <div style={{ marginBottom: 12 }}>
            <Input
              placeholder="Type"
              value={msgtype}
              onChange={(e) => setMsgType(e.target.value)}
              disabled={loadingTemplate}
            />
          </div> */}
        </Modal>
      </Spin>
    </div>
  );
};

export default View;
